import React from "react";
import {
  Page,
  Document,
  StyleSheet,
  View,
  Text,
  Image,
} from "@react-pdf/renderer";
import AifaLogo from "../../financial management/billings/Invoice/AIFA-Logo-2.png";
import { bankDetails } from "./bank_details";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    fontSize: 10,
    backgroundColor: "#f9f9f9",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    alignItems: "flex-start",
    fontSize: 10,
  },
  logo: {
    width: "30%",
  },
  title: {
    fontSize: 12,
    color: "black",
  },
  section: {
    marginBottom: 10,
    lineHeight: 1.1,
  },
  bold: {
    fontWeight: "bold",
    textAlign: "left",
    color: "#000",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "gray",
    marginBottom: 10,
    borderRadius:"5px"  
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    width: "20.5%",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "gray",
    padding: 10,
    fontSize: 8,
    fontWeight: "normal",
    flexDirection:"row"
  },
  tableHeader: {
    backgroundColor: "#d3e6f3",
    fontWeight: "bold",
  },
  bankDetails: {
    maxWidth: "50%",
    width: "100%",
    backgroundColor: "#d9edf7",
    padding: 10,
    marginBottom: 10,
    lineHeight: 1.1,
  },
  bankTitle: {
    color: "#009688",
    fontSize: 12,
    marginBottom: 5,
  },
  termsAndConditions: {
    marginTop: 10,
    lineHeight: 1.1,
    fontSize: 11,
    textAlign: "justify",
  },
  summaryTable: {
    maxWidth: "50%",
    width: "100%",
    display: "table",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "gray",
    marginBottom: 10,
  },
  summaryRow: {
    flexDirection: "row",
  },
  summaryCol: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "gray",
    padding: 18,
    fontSize: 10,
    fontWeight: "normal",
  },
  summaryRowTotal: {
    backgroundColor: "dodgerblue",
    color: "#fff",
    fontWeight: "bold",
  },
  heading: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "left",
    marginTop: 5,
    marginBottom: 7,
  },
  paragraph: {
    fontSize: 10,
    marginTop: 5,
    marginBottom: 5,
    textAlign: "left",
  },
  italicParagraph: {
    fontSize: 10,
    paddingTop: 5,
    paddingBottom: 5,
    fontStyle: "italic",
    textAlign: "left",
  },
  bulletPoint: {
    fontSize: 12,
    marginBottom: 8,
    lineHeight: 1.2,
    paddingLeft: 15,
    textAlign: "justify",
    listStyle: "circle",
  },
  footer: {
    fontSize: 10,
    textAlign: "center",
    marginTop: 20,
    color: "#666",
  },
  flexContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "stretch",
    alignContent: "flex-start",
  },
  flexContainerStart: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 5,
  },
  subject: {
    fontSize: 12,
    marginTop: 5,
    marginBottom: 10,
    textAlign: "left",
  },
  columnHeading: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 12,
    marginBottom: 2,
    textAlign: "left",
  },
  sectionColumnLeft: {
    maxWidth: "45%",
    width: "100%",
    marginBottom: 10,
    lineHeight: 1.1,
  },
  sectionColumnRight: {
    maxWidth: "45%",
    width: "100%",
    marginTop: 5,
    marginBottom: 10,
    lineHeight: 1.1,
  },
});

const parseHTML = (html) => {
  // Parse the HTML string into a DOM structure
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const pdfElements = [];
  let italicize = false;
  doc.body.childNodes.forEach((node) => {
    if (node.nodeName === "H2") {
      pdfElements.push(
        <Text key={pdfElements.length} style={styles.heading}>
          {node.textContent.trim()}
        </Text>
      );
    } else if (node.nodeName === "P") {
      const childBold = Array.from(node.childNodes).find(
        (child) => child.nodeName === "STRONG"
      );

      if (childBold) {
        pdfElements.push(
          <Text key={pdfElements.length} style={styles.paragraph}>
            <Text style={styles.bold}>{childBold.textContent}</Text>
          </Text>
        );
      } else if (
        Array.from(node.childNodes).some((child) => child.nodeName === "BR")
      ) {
        node.childNodes.forEach((child) => {
          if (child.nodeName === "#text") {
            pdfElements.push(
              <Text key={pdfElements.length} style={styles.paragraph}>
                {child.textContent.trim()}
              </Text>
            );
          } else if (child.nodeName === "BR") {
            pdfElements.push(
              <Text key={pdfElements.length} style={styles.paragraph}>
                {/* Line break */}
              </Text>
            );
          }
        });
      }
      else {
        const style = italicize ? styles.italicParagraph : styles.paragraph;
        pdfElements.push(
          <Text key={pdfElements.length} style={style}>
            {node.textContent.trim()}
          </Text>
        );

        if (node.textContent.includes("With Best Regards,")) {
          italicize = true;
        }
      }
    }
    else if (node.nodeName === "UL" || node.nodeName === "OL") {
      const listItems = Array.from(node.children).map((li, index) => (
        <Text key={index} style={styles.paragraph}>
          • {li.textContent}
        </Text>
      ));
      pdfElements.push(<View style={{ marginTop: 5 }}>{listItems}</View>);
    } 
  });
  return pdfElements;
};

const QuotationPDF = ({ quotation }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={AifaLogo} style={styles.logo} />
          <View style={styles.section}>
            <Text>
              <Text style={styles.bold}>Quotation No: </Text>
              {quotation?.quotationNo}
            </Text>
            <Text>
              <Text style={styles.bold}>Quotation Date: </Text>
              {quotation?.startDate}
            </Text>
            <Text>
              <Text style={styles.bold}>Valid Till: </Text>
              {quotation?.endDate}
            </Text>
          </View>
        </View>

        <Text style={styles.subject}>
          <Text style={styles.bold}>Subject: </Text>
          {quotation?.quotaion}
        </Text>

        <View style={styles.flexContainerStart}>
          <View style={styles.sectionColumnLeft}>
            <Text style={styles.columnHeading}>Quotation From</Text>
            <Text style={styles.bold}>
              AIFA Environmental Services & Pest Control LLC
            </Text>
            <Text>
              Del Ani Holding Building, 1st Floor F03, Al Quoz Industrial Area 3,
              Dubai, UAE
            </Text>
            <Text>
              <Text style={styles.bold}>VAT Number: </Text>
              100242271300003
            </Text>
            <Text>
              <Text style={styles.bold}>Email: </Text>
              aifaservicesdxb@gmail.com
            </Text>
            <Text>
              <Text style={styles.bold}>Phone: </Text>
              +971-48347261
            </Text>
            <Text>
              <Text style={styles.bold}>Website: </Text>
              aifaservices.ae
            </Text>
          </View>
          <View style={styles.sectionColumnRight}>
            <Text style={styles.columnHeading}>Quotation For</Text>
            <Text>
              {quotation?.customer.firstname} {quotation?.customer.lastname}
            </Text>
            <Text>{quotation?.customer.mailingAddress}</Text>
            <Text>
              <Text style={styles.bold}>Phone: </Text>
              {quotation?.customer.phone}
            </Text>
            <Text>
              <Text style={styles.bold}>Email: </Text>
              {quotation?.customer.email || "Empty"}
            </Text>
            <Text>
              <Text style={styles.bold}>VAT Number: </Text>
              {quotation?.customer.TRN || "Empty"}
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={{...styles.tableCol,...styles.bold}}>Item</Text>
            <Text style={{...styles.tableCol,...styles.bold}}>Description</Text>
            <Text style={{...styles.tableCol,...styles.bold}}>VAT Rate</Text>
            <Text style={{...styles.tableCol,...styles.bold}}>Quantity</Text>
            <Text style={{...styles.tableCol,...styles.bold}}>Rate</Text>
            {/* <Text style={{...styles.tableCol,...styles.bold}}>Discount</Text> */}
            <Text style={{...styles.tableCol,...styles.bold}}>Amount</Text>
            {/* <Text style={{...styles.tableCol,...styles.bold}}>VAT</Text> */}
            <Text style={{...styles.tableCol,...styles.bold}}>Total</Text>
          </View>

          {quotation?.subSeriviceList.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <Text style={{...styles.tableCol,...styles.bold}}>
                {item.subService.name}
              </Text>
           <Text style={styles.tableCol}>
                {item?.detail}
              </Text>
              <Text style={styles.tableCol}>5%</Text>
              <Text style={styles.tableCol}>{item.quantity}</Text>
              <Text style={styles.tableCol}>
              {(item.subTotal / item.quantity).toFixed(2)} AED
              </Text>
              {/* <Text style={styles.tableCol}>
                {item.discountAmount} AED
              </Text> */}
              <Text style={styles.tableCol}>
                {item.priceAfterDiscount} AED
              </Text>
              {/* <Text style={styles.tableCol}>
                {item.vatAmount} AED
              </Text> */}
              <Text style={styles.tableCol}>
                {item.netPrice} AED
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.flexContainer}>
        <View style={styles.bankDetails}>
            <Text style={styles.bankTitle}>Bank Details</Text>
            <Text>
              <Text style={styles.bold}>Account Number: </Text>
            {bankDetails.ACCOUNTNO}
            </Text>
            <Text>
              <Text style={styles.bold}>Account Name: </Text>
              <Text>{ bankDetails.AccountName}</Text>
            </Text>
            <Text>
              <Text style={styles.bold}>IBAN NO : </Text>
            { bankDetails.IBANNO}
            </Text>
            <Text>
              <Text style={styles.bold}>SWIFT CODE: </Text>
               { bankDetails.SWIFTCODE}
            </Text>
            <Text>
              <Text style={styles.bold}>BANK NAME: </Text>
              { bankDetails.BANKNAME}
            </Text>
            <Text>
              <Text style={styles.bold}>BRANCH: </Text>
              {bankDetails.BRANCHNAME}
            </Text>
            <Text>
              <Text style={styles.bold}>BANK ADDRESS: </Text>
          {bankDetails.BANKADDRESS}
            </Text>
            <Text>
              <Text style={styles.bold}>BANK TEL NO: </Text>
         {bankDetails.BANKTELNO}
            </Text>
          </View>
          <View style={styles.summaryTable}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryCol}>Sub Total</Text>
              <Text style={styles.summaryCol}>
                {quotation?.pricesCalculation?.subtotal} AED
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryCol}>Discount</Text>
              <Text style={styles.summaryCol}>
                {quotation?.pricesCalculation?.EstimatedDiscounts} AED
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryCol}>Amount</Text>
              <Text style={styles.summaryCol}>
                {quotation?.pricesCalculation?.beforeDiscountSubtotal} AED
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryCol}>VAT</Text>
              <Text style={styles.summaryCol}>
                {quotation?.pricesCalculation?.EstimatedTax} AED
              </Text>
            </View>
            <View style={[styles.summaryRow, styles.summaryRowTotal]}>
              <Text style={styles.summaryCol}>Total (AED)</Text>
              <Text style={styles.summaryCol}>
                {quotation?.pricesCalculation?.total} AED
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.termsAndConditions}>
          {parseHTML(quotation?.discription.replace(//g, ""))}
        </View>
      </Page>
    </Document>
  );
};

export default QuotationPDF;
